import { HttpParams } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbDropdown, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { OirHttpService } from '../../services/http/oir-http.service';
import {
  TypeaheadModel,
  TypeaheadOptions,
  TypeaheadValue,
} from './typeahead.model';

export enum KeyPress {
  Tab = 9,
  Enter = 13,
}

@Component({
  selector: 'oir-typeahead',
  templateUrl: './typeahead.component.html',
  styleUrls: ['./typeahead.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TypeaheadComponent),
      multi: true,
    },
  ],
})
export class TypeaheadComponent
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() apiPath: string;
  @Input() apiQuery = {};
  @Input() model: any;
  @Input() values: TypeaheadValue<any>[] = [];
  @Input() placeholder = 'Start typing';
  @Input() disabled = false;
  @Input() options: TypeaheadOptions;
  @Input() params: HttpParams;

  @Output() readonly modelChange: EventEmitter<any> = new EventEmitter();
  @Output() onNoResultsFound: EventEmitter<string> = new EventEmitter();

  @ViewChild(NgbDropdown, { static: true }) dropdown: NgbDropdown;
  @ViewChild('input', { static: true }) input: ElementRef;
  @ViewChild(NgbDropdownToggle, { static: true }) ddt: NgbDropdownToggle;
  @ViewChild('inputPills', { static: true }) inputFieldTypeAhead: ElementRef;
  @ViewChildren('inputs') inputs: QueryList<ElementRef>;

  pillEnteredValue: TypeaheadValue<any>;
  inputDisabled = false;
  query = '';
  data: TypeaheadValue<any>[];
  typeaheadModel: TypeaheadModel;
  maxHeightPx = 508;
  maxHeight = this.maxHeightPx;

  protected debounce: Observable<any>;
  private listener: () => void;
  private readonly inputPadding = 11;
  protected onSearchSubscription: Subscription;

  propagateChange = (_: TypeaheadModel) => {};
  propagateTouch = (_: any) => {};

  constructor(
    protected el: ElementRef,
    protected renderer: Renderer2,
    protected http: OirHttpService,
    protected cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.typeaheadModel = {
      active: false,
      animating: false,
      processing: false,
      spinnerActive: false,
    };
    this.options = this.options || {
      pills: false,
      modelPropertyName: 'value',
      titlePropertyName: 'title',
      responsePropertyName: 'result',
      useREST: true,
      returnModelProperty: null,
      maxPills: 0,
      pillsOnEnter: false
    };
    if (!this.options.maxPills) {
      this.options.maxPills = 0;
    }

    if (this.options.pills && !Array.isArray(this.model)) {
      this.model = [];
    }
    if (this.input) {
      this.debounce = fromEvent(this.input.nativeElement, 'keyup').pipe(
        // Don't search when enter or tab keys are pressed.
        map((i: any) =>
          i.which === KeyPress.Tab || i.which === KeyPress.Enter
            ? null
            : i.currentTarget.value
        ),
        debounceTime(500)
      );
      this.debounce.subscribe((val) => {
        if (val !== null) {
          this.onSearch(val);
        }
      });
    }


  }

  ngOnDestroy() {
    this.unsubscribeOnSearch();
  }

  onEscapePress(event: KeyboardEvent): void {
    if (event.code === 'Escape') {
      this.dropdown.close();  
      this.input.nativeElement.value='';
      this.input.nativeElement.focus();
    }
  }

  resizeInput(event: KeyboardEvent): void {
    if (event) {
      const min = 250;
      const padRight = 5;
      const input = event.target as HTMLInputElement;
      // Create temporary element with same styling as input.
      const tmp = document.createElement('div');
      tmp.style.padding = '0';
      tmp.style.font = '400 15px/21.4286px Montserrat-Light';
      tmp.style.paddingLeft = '8px';
      tmp.style.paddingRight = '8px';
      tmp.style.width = '';
      tmp.style.position = 'absolute';
      tmp.innerHTML = input.value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
        .replace(/ /g, '&nbsp;');
      input.parentNode.parentNode.appendChild(tmp);
      const width = tmp.clientWidth + padRight + 1;
      tmp.parentNode.removeChild(tmp);
      if (
        min <= width &&
        this.el.nativeElement.getBoundingClientRect().width -
        this.inputPadding >=
        width
      ) {
        input.style.width = `${width}px`;
      }
    }
  }

  stateChange(isOpen: boolean): void {
    if (isOpen) {
      const ddBounds = this.ddt.nativeElement.getBoundingClientRect();
      const body = document.querySelector('#body.body') as HTMLElement;
      // Get visible window port height and minus the element height and distance to bottom of viewport.
      if (body) {
        const maxHeight: number =
          body.offsetHeight - (ddBounds.top + ddBounds.height);
        this.maxHeight =
          maxHeight < this.maxHeightPx ? maxHeight : this.maxHeightPx;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.dropdown) {
      this.dropdown.placement = 'bottom';
    }
    this.options = this.options || {
      pills: false,
      modelPropertyName: 'value',
      titlePropertyName: 'title',
      responsePropertyName: 'result',
      useREST: true,
      returnModelProperty: null,
      maxPills: 0,
      pillsOnEnter: false,
    };

    if (changes.model) {
      if (this.options.pills) {
        this.query =
          !changes.model.currentValue ||
          (changes.model.currentValue && !changes.model.currentValue.length)
            ? ''
            : changes.model.currentValue[this.options.titlePropertyName];
      } else {
        this.query =
          !changes.model.currentValue ||
          (changes.model.currentValue &&
            // eslint-disable-next-line no-prototype-builtins
            !changes.model.currentValue.hasOwnProperty(
              this.options.modelPropertyName
            ))
            ? ''
            : changes.model.currentValue[this.options.titlePropertyName];
      }
    }
  }

  registerOnChange(fn): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn): void {
    this.propagateTouch = fn;
  }

  writeValue(obj: any): void {
    if (!obj) {
      obj = this.options.pills ? [] : '';
    }
    this.model = obj;
    if (typeof obj.value === 'object') {
      this.query = obj.value[this.options.titlePropertyName];
      if (this.query === undefined) {
        this.query = obj[this.options.titlePropertyName];
      }
    } else {
      this.query = obj[this.options.titlePropertyName];
    }
    if (this.query === undefined) {
      this.query = '';
    }
    this.inputDisabled =
      this.options.pills &&
      Array.isArray(this.model) &&
      this.options.maxPills !== 0 &&
      this.model.length >= this.options.maxPills;
    this.cd.detectChanges();
  }

  setDisabledState(value: any): void {
    this.disabled = value;
  }

  removePill($event: any, option: any): void {
    // Button click has screen x and y of 0;
    const isButtonPress = $event.screenX === 0 && $event.screenY === 0;
    if (!this.disabled) {
      option.animate = true;

      setTimeout(() => {
        option.selected = false;
        option.animate = false;

        let selectedIndex = 0;
        if (isButtonPress) {
          // Get index of currently removing pill.
          const itemRemoved = this.model.filter(
            (x) =>
              x[this.options.modelPropertyName] ===
              option[this.options.modelPropertyName]
          )[0];
          this.inputs.forEach((child, index) => {
            if (
              child.nativeElement.textContent.trim() ===
              itemRemoved[this.options.titlePropertyName]
            ) {
              selectedIndex = index;
            }
          });
        }

        // Remove item from model.
        this.model = this.model.filter(
          (x) =>
            x[this.options.modelPropertyName] !==
            option[this.options.modelPropertyName]
        );

        this.propagateChange(this.model);
        this.modelChange.emit(this.model);
        this.inputDisabled =
          Array.isArray(this.model) &&
          this.options.maxPills !== 0 &&
          this.model.length >= this.options.maxPills;

        // Focus on pill before deleted pill or next if it is the first one and button press.
        if (isButtonPress && this.inputs.length > 1) {
          if (selectedIndex === 0) {
            selectedIndex = 2;
          }
          this.inputs.toArray()[selectedIndex - 1].nativeElement.focus();
        }
      }, 200);

      $event.stopPropagation();
    }
  }

  showPills(): boolean {
    return Array.isArray(this.model) && this.options.pills === true;
  }

  blurInput(event): void {
    if (
      event.which === KeyPress.Tab &&
      this.data &&
      (this.data.length === 0 || this.query === '')
    ) {
      this.close();
    }
    if (
      event.which === KeyPress.Enter &&
      this.query.trim() !== '' &&
      this.options.pillsOnEnter &&
      this.options.maxPills > 0
    ) {
      if (
        this.model.find(
          (x) =>
            x[this.options.titlePropertyName].toString().toLowerCase() ===
            this.query.trim().toLowerCase()
        ) === undefined
      ) {
        const enteredValue: any = {};
        const textQuery = this.query.trim();
        enteredValue[this.options.titlePropertyName] = textQuery;
        enteredValue[this.options.modelPropertyName] = textQuery;
        this.pillEnteredValue = { value: enteredValue, title: this.query };
        let validateQuery;
        if (this.data !== undefined) {
          validateQuery = this.data.filter(
            (x) =>
              x[this.options.titlePropertyName].toString().toLowerCase() ===
              textQuery.toLowerCase()
          )[0];
        }
        validateQuery !== undefined
          ? this.select(validateQuery, false)
          : this.select(this.pillEnteredValue, true);
      } else {
        this.query = '';
      }
    }
    this.resizeInput(event);
  }

  close(): void {
    if (this.typeaheadModel.active) {
      this.typeaheadModel.active = false;
      this.typeaheadModel.animating = true;
      this.dropdown.close();
      setTimeout(() => {
        this.typeaheadModel.animating = false;
      }, 280);
    }
  }

  optionKeyDown(event: KeyboardEvent, option: TypeaheadValue<any>): void {
    if (event.which === KeyPress.Enter) {
      this.select(option);
    } else if (event.which === KeyPress.Tab) {
      this.dropdown.close();
    }
  }

  select(option: TypeaheadValue<any>, enteredValue = false): void {

    if (this.inputFieldTypeAhead && !enteredValue) {
      // Removes the input width after item selected.
      this.renderer.setAttribute(
        this.inputFieldTypeAhead.nativeElement,
        'style',
        undefined
      );
    }
    if (!option.disabled && !option.selected) {
      const value = this.getCurrentValue(option, enteredValue);
      // If object doesn't exist add it.
      if (
        !this.options.pills ||
        (this.options.pills &&
          this.model.filter(
            (x) =>
              JSON.stringify(
                x[this.options.titlePropertyName]
                  .toString()
                  .toLowerCase()
                  .trim()
              ) ===
              JSON.stringify(
                value[this.options.titlePropertyName]
                  .toString()
                  .toLowerCase()
                  .trim()
              )
          ).length === 0 &&
          (this.options.maxPills === 0 ||
            this.model.length < this.options.maxPills))
      ) {
        if (this.options.pills) {
          this.query = '';
          if (!value[this.options.titlePropertyName]) {
            this.model.push(option);
          } else {
            this.model.push(value);
          }
          this.inputDisabled =
            Array.isArray(this.model) &&
            this.options.maxPills !== 0 &&
            this.model.length >= this.options.maxPills;
        } else if (!this.options.pills) {
          this.query = option.title;
          if (!value[this.options.titlePropertyName]) {
            this.model = option;
          } else {
            this.model = value;
          }
        }

        // Return only if not using pills and return property asked.
        if (!this.options.pills && this.options.returnModelProperty) {
          this.propagateChange(this.model[this.options.returnModelProperty]);
        } else {
          this.propagateChange(this.model);
        }
        this.modelChange.emit(this.model);
        this.input.nativeElement.focus();
        this.close();
        this.data = [];
      } else {
        this.query = '';
      }
    }
  }

  /**
   * Method to get placeholder value - in case none also hide input
   */
  public getPlaceHolder(): string {
    if (
      this.options &&
      this.options.maxPills &&
      this.model &&
      this.model.length &&
      this.options.maxPills === this.model.length
    ) {
      this.renderer.addClass(this.inputFieldTypeAhead.nativeElement, 'hidden');
      return '';
    }
    
    this.renderer.removeClass(
      this.inputFieldTypeAhead.nativeElement,
      'hidden'
    );

    return this.placeholder;
  }

  private onSearch(query): void {
    if (query !== '') {
      this.typeaheadModel.spinnerActive = true;
      // If custom data passed in search through that otherwise call API,
      if (this.values.length > 0) {
        this.data = [];
        const results = this.values.filter(
          (x) => x.title.toLowerCase().indexOf(this.query.toLowerCase()) >= 0
        );
        if (results && results.length > 0) {
          for (const result of results) {
            this.data.push({ value: result.value, title: result.title });
          }
          this.toggleDropdown();
          this.typeaheadModel.active = true;
          this.clearActive();
          this.registerListener();
        } else {
          this.onNoResultsFound.emit(query);
        }
      } else {
        if (this.apiPath) {
          this.unsubscribeOnSearch();
          this.onSearchSubscription = this.http
            .get<any>({
              path: this.apiPath,
              query: {
                ...this.apiQuery,
                query: this.query
              },
              hideSpinner: true
            })
            .pipe(map((response) => this.getResponseObject(response)))
            .subscribe((response) => {
              this.data = [];

              if (response && response.length) {
                const titleProp = this.options.titlePropertyName;
                this.data = response
                  .slice(0, 10)
                  .map((x) => ({
                    value: x,
                    title: x[titleProp],
                  }));
                this.data = this.data.sort(function(a, b) {
                  return a[titleProp].localeCompare(b[titleProp]);
                });
              }
              this.toggleDropdown();
              this.typeaheadModel.active = true;
              this.clearActive();
              this.registerListener();
            });
        } else {
          this.data = [];
          this.toggleDropdown();
          this.cd.detectChanges();
          this.typeaheadModel.active = true;
          this.clearActive();
          this.registerListener();
        }
      }
    } else {
      this.query = '';
      if (!this.options.pills) {
        this.model = [];

        this.propagateChange(this.model);
        this.modelChange.emit(this.model);
      }

      this.close();
    }
  }

  protected getResponseObject(response: any): any {
    let result = response;

    if (!Array.isArray(response) && this.options.responsePropertyName) {
      const split = this.options.responsePropertyName.split('.');

      for (const s of split) {
        result = result[s];
      }
    }

    return result;
  }

  protected clearActive(): void {
    this.typeaheadModel.processing = false;
    this.typeaheadModel.spinnerActive = false;
  }

  protected getCurrentValue(
    option: TypeaheadValue<any>,
    enteredValue = false
  ): any {
    let result: any;

    if (typeof option.value === 'object') {
      result = enteredValue
        ? option.value
        : this.data.find(
          (x) =>
            x.value[this.options.modelPropertyName] ===
            option.value[this.options.modelPropertyName]
        ).value;
    } else {
      result = option;
    }

    return result;
  }

  protected registerListener(): void {
    this.listener = this.renderer.listen('document', 'click', (event) => {
      if (!this.el.nativeElement.contains(event.target)) {
        this.close();
        this.listener();
      }
    });
  }

  protected unsubscribeOnSearch() {
    if (this.onSearchSubscription) {
      this.onSearchSubscription.unsubscribe();
      this.onSearchSubscription = null;
    }
  }

  protected toggleDropdown() {
    if (this.dropdown.isOpen()) {
      this.dropdown.close();
    }
    this.dropdown.open();
  }
}
